import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Camera, Eye, EyeOff, Loader2, Plus } from 'lucide-react';
import {
  addStageEditorRow,
  durationEditorShape,
  followSavedStageRows,
  formatApiError,
  moveStageEditorRow,
  resetStageEditorRows,
  stageEditorDurations,
  stageEditorInput,
  stageEditorPosition,
  stageEditorProblem,
  stageEditorRows,
  stageEditorShape,
  withServerStageKeys,
  type StageEditorRow,
} from '@mustauto/utils';
import { formatStageDuration } from '@mustauto/types';
import { Card, CardContent, CardHeader, CardTitle, ErrorState, Skeleton, cn, useToast } from '@mustauto/ui/web';
import { useWorkshopStages, type WorkshopStagesPayload } from '@/hooks/useSettings';

type Row = StageEditorRow;
type ModulePayload = WorkshopStagesPayload['modules'][number];

/**
 * Configurações → Etapas (F3.8). Decisão do dono do produto (04/10): o gestor
 * renomeia, reordena, esconde e cria as etapas, escolhe em quais o mecânico
 * tira foto ao entrar e quanto cada uma costuma levar (F3.7) — tudo na mesma
 * linha, e quando quiser.
 *
 * O que a tela precisa deixar claro, porque o servidor garante mas o gestor
 * não vê: etapa não se apaga, se esconde (a OS que está nela continua nela);
 * esconder a cabine desliga o card, a fila e a foto antes da pintura; e nada
 * disso muda uma OS sozinho.
 *
 * Só aparece para oficina com funilaria ou estética ligada.
 */
export function StagesCard({ readOnly }: { readOnly: boolean }) {
  const { data, isLoading, isError, error, refetch } = useWorkshopStages();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-2 pt-6" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <ErrorState error={error} onRetry={() => refetch()} />
        </CardContent>
      </Card>
    );
  }
  if (data.modules.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Etapas</CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        <p className="text-xs text-muted-foreground">
          A ordem é a que o mecânico vê e a que o cliente acompanha (&quot;etapa 3 de 6&quot;). Com a câmera
          ligada, o mecânico tira a foto ao levar o carro para a etapa. O tempo é opcional: com ele, cada OS
          mostra até quando a etapa deve terminar. As mudanças valem para as próximas trocas de etapa — nenhuma OS
          muda sozinha.
        </p>
        {data.modules.map((m) => (
          <ModuleStages key={m.module} module={m} limits={data.limits} choices={data.durationChoices} readOnly={readOnly} />
        ))}
        {readOnly && <p className="text-xs text-muted-foreground">Só o gestor da oficina muda as etapas.</p>}
      </CardContent>
    </Card>
  );
}

function ModuleStages({
  module,
  limits,
  choices,
  readOnly,
}: {
  module: ModulePayload;
  limits: WorkshopStagesPayload['limits'];
  choices: number[];
  readOnly: boolean;
}) {
  const { toast } = useToast();
  const { saveStages, saveDurations } = useWorkshopStages();
  const saved = useMemo(() => stageEditorRows(module.stages), [module]);
  const [rows, setRows] = useState<Row[]>(saved);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<'hide-booth' | 'reset' | null>(null);

  // O formulário acompanha o servidor só enquanto o gestor não mexeu nele
  // (`followSavedStageRows`): voltar para a aba relê, e resetar ali jogava
  // fora as edições sem aviso.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const previousSaved = useRef(saved);
  useEffect(() => {
    setRows(followSavedStageRows(rowsRef.current, previousSaved.current, saved));
    previousSaved.current = saved;
  }, [saved]);

  const stagesDirty = stageEditorShape(rows) !== stageEditorShape(saved);
  const durationsDirty = durationEditorShape(rows) !== durationEditorShape(saved);
  const dirty = stagesDirty || durationsDirty;
  const invalid = stageEditorProblem(rows);
  const disabled = readOnly || saving;

  const update = (uid: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

  const move = (index: number, delta: -1 | 1) => setRows((prev) => moveStageEditorRow(prev, index, delta));

  const toggleHidden = (row: Row) => {
    if (!row.hidden && row.isPaintBooth) {
      setConfirm('hide-booth');
      return;
    }
    update(row.uid, { hidden: !row.hidden });
  };

  const add = () => setRows((prev) => addStageEditorRow(prev).rows);

  const reset = () => {
    setRows(resetStageEditorRows(module.module, module.defaults, rows));
    setConfirm(null);
  };

  const run = async () => {
    setSaving(true);
    let stagesSaved = false;
    try {
      let keyed = rows;
      if (stagesDirty) {
        const result = await saveStages({ module: module.module, stages: stageEditorInput(rows) });
        stagesSaved = true;
        keyed = withServerStageKeys(rows, result.modules.find((m) => m.module === module.module)?.stages ?? []);
        setRows(keyed);
      }
      if (durationsDirty) {
        await saveDurations({ module: module.module, durations: stageEditorDurations(keyed), complete: true });
      }
      toast({ type: 'success', title: `Etapas de ${module.label.toLowerCase()} salvas` });
    } catch (err) {
      toast({
        type: 'error',
        title: stagesSaved ? 'As etapas foram salvas, mas os tempos não' : 'Não foi possível salvar',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    } finally {
      setSaving(false);
    }
  };

  const booth = rows.find((r) => r.isPaintBooth);

  return (
    <section aria-label={`Etapas de ${module.label}`} className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{module.label}</h3>
        <span className="text-xs text-muted-foreground">{module.customized ? 'Do jeito da oficina' : 'Etapas padrão'}</span>
      </div>

      <ol className="divide-y rounded-lg border border-input">
        {rows.map((r, i) => {
          const position = stageEditorPosition(rows, i);
          return (
            <li
              key={r.uid}
              className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2', r.hidden && 'bg-muted/50')}
            >
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={disabled || i === 0}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label={`Subir ${r.label || 'etapa nova'}`}
                >
                  <ArrowUp size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={disabled || i === rows.length - 1}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label={`Descer ${r.label || 'etapa nova'}`}
                >
                  <ArrowDown size={14} aria-hidden />
                </button>
              </div>
              <span className="w-5 text-center text-sm font-medium tabular-nums text-muted-foreground">{position ?? '–'}</span>

              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <input
                  value={r.label}
                  onChange={(e) => update(r.uid, { label: e.target.value })}
                  maxLength={limits.maxLabelLength}
                  disabled={disabled}
                  placeholder="Nome da etapa"
                  aria-label={`Nome da etapa ${position ?? 'escondida'}`}
                  className={cn(
                    'h-8 w-full max-w-60 rounded-md border border-input bg-background px-2 text-sm',
                    r.hidden && 'text-muted-foreground line-through',
                  )}
                />
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  {!r.key && <Tag>Nova</Tag>}
                  {r.defaultLabel && r.defaultLabel !== r.label.trim() && <Tag>padrão: {r.defaultLabel}</Tag>}
                  {r.isPaintBooth && <Tag>A cabine</Tag>}
                  {r.hidden && (
                    <Tag warn>
                      Escondida{r.openOrders > 0 ? ` · ${r.openOrders} OS nela agora` : ''}
                    </Tag>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => update(r.uid, { photo: !r.photo })}
                disabled={disabled}
                aria-pressed={r.photo}
                aria-label={`Pedir foto ao entrar em ${r.label || 'etapa nova'}`}
                title={r.photo ? 'Pede foto ao entrar' : 'Não pede foto'}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-md border border-input',
                  r.photo ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                <Camera size={14} aria-hidden />
              </button>

              <select
                className="h-8 w-32 rounded-md border border-input bg-background px-2 text-sm"
                value={r.hours ?? ''}
                disabled={disabled}
                onChange={(e) => update(r.uid, { hours: e.target.value ? Number(e.target.value) : null })}
                aria-label={`Tempo da etapa ${r.label || 'nova'}`}
              >
                <option value="">Sem previsão</option>
                {choices.map((h) => (
                  <option key={h} value={h}>
                    {formatStageDuration(h)}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => toggleHidden(r)}
                disabled={disabled}
                aria-pressed={!r.hidden}
                aria-label={r.hidden ? `Mostrar ${r.label}` : `Esconder ${r.label || 'etapa nova'}`}
                title={r.hidden ? 'Mostrar de novo' : 'Esconder'}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-input text-muted-foreground hover:text-foreground"
              >
                {r.hidden ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
              </button>
            </li>
          );
        })}
        <li>
          <button
            type="button"
            onClick={add}
            disabled={disabled || rows.length >= limits.maxStages}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-primary hover:underline disabled:opacity-50"
          >
            <Plus size={14} aria-hidden /> Nova etapa
          </button>
        </li>
      </ol>

      {confirm === 'hide-booth' && booth && (
        <div role="alertdialog" aria-label={`Esconder ${booth.label}?`} className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
          <p className="font-semibold">Esconder {booth.label}?</p>
          <p className="mt-1">É a etapa da cabine de pintura. Ao esconder:</p>
          <ul className="ml-5 mt-1 list-disc">
            <li>o card da cabine sai do painel e do app do gestor;</li>
            <li>a fila da cabine deixa de existir;</li>
            <li>o mecânico deixa de tirar a foto antes da pintura.</li>
          </ul>
          <p className="mt-1">As fotos que já existem continuam nas OS. Mostrar a etapa de novo religa tudo.</p>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirm(null)} className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground">
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                update(booth.uid, { hidden: true });
                setConfirm(null);
              }}
              className="h-8 rounded-md border border-amber-700 px-3 text-sm font-medium text-amber-800 dark:border-amber-400 dark:text-amber-300"
            >
              Esconder {booth.label}
            </button>
          </div>
        </div>
      )}

      {confirm === 'reset' && (
        <div role="alertdialog" aria-label="Voltar às etapas padrão?" className="rounded-lg border border-input bg-muted/50 p-3 text-sm">
          <p className="font-semibold">Voltar às etapas padrão?</p>
          <p className="mt-1">
            Os nomes e a ordem voltam ao padrão. As etapas criadas pela oficina ficam escondidas, e nenhuma OS muda de etapa. Os
            tempos ficam como estão.
          </p>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirm(null)} className="h-8 rounded-md px-3 text-sm text-muted-foreground hover:text-foreground">
              Cancelar
            </button>
            <button type="button" onClick={reset} className="h-8 rounded-md border border-input px-3 text-sm font-medium">
              Voltar ao padrão
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setConfirm('reset')}
          disabled={disabled || !module.customized}
          className="h-8 text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          Voltar às etapas padrão
        </button>
        <div className="flex items-center gap-3">
          {invalid && dirty && <span className="text-xs text-amber-700 dark:text-amber-300">{invalid}</span>}
          <button
            type="button"
            onClick={() => void run()}
            disabled={disabled || !dirty || invalid !== null}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
          >
            {saving && <Loader2 size={14} className="animate-spin" aria-hidden />}
            Salvar {module.label.toLowerCase()}
          </button>
        </div>
      </div>
    </section>
  );
}

function Tag({ children, warn }: { children: ReactNode; warn?: boolean }) {
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 font-medium',
        warn ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' : 'bg-muted text-muted-foreground',
      )}
    >
      {children}
    </span>
  );
}
